import * as React from 'react';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

type TriggerProps = Omit<
  React.ComponentProps<typeof TooltipTrigger>,
  'children' | 'asChild' | 'title'
>;

type TooltipRootProps = Omit<
  React.ComponentProps<typeof Tooltip>,
  'children'
>;

type TooltipProviderProps = Omit<
  React.ComponentProps<typeof TooltipProvider>,
  'children' | 'delayDuration'
>;

type TooltipContentAdditionalProps = Omit<
  TooltipContentProps,
  'children' | 'side' | 'align' | 'className'
>;

export interface TooltipWrapperProps extends TriggerProps {
  children: React.ReactNode;
  content?: React.ReactNode;
  side?: TooltipContentProps['side'];
  align?: TooltipContentProps['align'];
  delay?: number;
  disabled?: boolean;
  title?: React.ReactNode;
  placement?: TooltipContentProps['side'];
  fullTitle?: boolean;
  maxLength?: number;
  tooltipProps?: TooltipRootProps;
  providerProps?: TooltipProviderProps;
  contentProps?: TooltipContentAdditionalProps & { className?: string };
}

interface TriggerWrapperProps extends TriggerProps {
  className?: string;
  tabIndex?: number;
  role?: string;
  children: React.ReactElement;
}

const BLOCK_LEVEL_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'dd', 'details', 'div', 'dl', 'dt',
  'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section',
  'table', 'ul',
]);

function getTriggerLayoutClass(element: React.ReactElement) {
  const className = typeof element.props?.className === 'string' ? element.props.className : '';
  const tokens = new Set(className.split(/\s+/).filter(Boolean));
  const isBlockTag = typeof element.type === 'string' && BLOCK_LEVEL_TAGS.has(element.type);
  const isFlexItem = tokens.has('tw-flex-1') || tokens.has('tw-flex-auto') || tokens.has('tw-grow');
  const isInline = tokens.has('tw-inline') || tokens.has('tw-inline-block') || tokens.has('tw-inline-flex');

  if (isFlexItem) {
    return 'tw-block tw-min-w-0 tw-max-w-full tw-flex-1';
  }

  if (!isInline && (
    isBlockTag
    || tokens.has('tw-block')
    || tokens.has('tw-flex')
    || tokens.has('tw-grid')
    || tokens.has('tw-w-full')
  )) {
    const fillsParent = tokens.has('tw-w-full') || tokens.has('tw-block');
    return cn(
      'tw-block tw-max-w-full',
      fillsParent && 'tw-w-full',
      (tokens.has('tw-min-w-0') || tokens.has('tw-truncate')) && 'tw-min-w-0',
    );
  }

  return 'tw-inline-flex tw-items-center tw-align-middle tw-max-w-full';
}

const TooltipTriggerWrapper = React.forwardRef<HTMLSpanElement, TriggerWrapperProps>(
  (
    {
      children, className, tabIndex, role, ...triggerProps
    },
    forwardedRef
  ) => {
    const childElement = React.Children.only(children);
    const forwardedRefRef = React.useRef(forwardedRef);
    React.useLayoutEffect(() => {
      forwardedRefRef.current = forwardedRef;
    }, [forwardedRef]);

    // Radix Slot builds a new ref callback each render. A changing callback makes React
    // detach the anchor (null) and attach it again, and PopperAnchor setStates on that change.
    const stableRef = React.useCallback((node: HTMLSpanElement | null) => {
      const ref = forwardedRefRef.current;
      if (typeof ref === 'function') {
        ref(node);
        return;
      }
      if (ref) {
        (ref as React.MutableRefObject<HTMLSpanElement | null>).current = node;
      }
    }, []);

    const isIntrinsicElement = typeof childElement.type === 'string';
    const isSvgElement = isIntrinsicElement && childElement.type === 'svg';

    return (
      <span
        {...triggerProps}
        ref={stableRef}
        className={cn(getTriggerLayoutClass(childElement), className)}
        tabIndex={tabIndex ?? (isIntrinsicElement && !isSvgElement ? undefined : 0)}
        role={role ?? (isIntrinsicElement && !isSvgElement ? undefined : 'button')}
      >
        {childElement}
      </span>
    );
  }
);

function resolveContent(
  content: React.ReactNode,
  title: React.ReactNode,
  fullTitle: boolean,
  maxLength: number
) {
  const baseContent = content ?? title ?? null;

  if (
    typeof baseContent === 'string' &&
    !fullTitle &&
    baseContent.length > maxLength
  ) {
    return `${baseContent.substring(0, maxLength)}...`;
  }

  return baseContent;
}

function TooltipWrapperComponent({
  children,
  content,
  title,
  side,
  align = 'center',
  delay = 0,
  disabled = false,
  placement,
  fullTitle = false,
  maxLength = 150,
  tooltipProps,
  providerProps,
  contentProps,
  className,
  tabIndex,
  role,
  ...triggerProps
}: TooltipWrapperProps) {
  if (!children) {
    return null;
  }

  // Normalize children: filter out null/undefined/false and ensure proper structure
  const normalizedChildren = React.Children.toArray(children).filter(
    (child) => child != null && child !== false
  );

  if (normalizedChildren.length === 0) {
    return null;
  }

  if (disabled) {
    // Return children with proper keys if it's an array
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            // Preserve key if child already has one, otherwise use index
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  const resolvedContent = resolveContent(
    content,
    title,
    fullTitle,
    maxLength
  );

  if (!resolvedContent) {
    // Return children with proper keys if it's an array
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            // Preserve key if child already has one, otherwise use index
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  const { className: contentClassName, ...contentRest } = contentProps ?? {};
  const resolvedSide = side ?? placement ?? 'top';

  // TooltipTriggerWrapper expects a single child, so use the first one
  const firstChild = normalizedChildren[0];
  const childElement: React.ReactElement = React.isValidElement(firstChild)
    ? (firstChild as React.ReactElement)
    : React.createElement('span', null, firstChild);

  // Normalize resolvedContent to handle arrays and null values
  const normalizedContent = React.Children.toArray(resolvedContent).filter(
    (item) => item != null && item !== false
  );

  // If content is empty after normalization, return children without tooltip
  if (normalizedContent.length === 0) {
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  return (
    <Tooltip delayDuration={delay} providerProps={providerProps} {...tooltipProps}>
      <TooltipTrigger asChild>
        <TooltipTriggerWrapper
          {...triggerProps}
          className={className}
          tabIndex={tabIndex}
          role={role}
        >
          {childElement}
        </TooltipTriggerWrapper>
      </TooltipTrigger>
      <TooltipContent
        side={resolvedSide}
        align={align}
        className={contentClassName}
        {...contentRest}
      >
        {normalizedContent.length > 1 ? (
          normalizedContent.map((item, index) => {
            const key = React.isValidElement(item) && item.key != null
              ? item.key
              : `tooltip-content-${index}`;
            return <React.Fragment key={key}>{item}</React.Fragment>;
          })
        ) : (
          normalizedContent[0]
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export const TooltipWrapper = React.memo(TooltipWrapperComponent);

export default TooltipWrapper;

